import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    Tag,
    Space,
    message,
    Popconfirm,
    Row,
    Col,
    Statistic,
    Typography,
    Divider,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import api from '../../../services/api';
import { getStatusColor } from '../../../utils/ui';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Integrations = () => {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingIntegration, setEditingIntegration] = useState(null);
    const [form] = Form.useForm();
    const [stats, setStats] = useState({});
    const [types, setTypes] = useState({});

    useEffect(() => {
        fetchIntegrations();
        fetchStats();
        fetchTypes();
    }, []);

    const fetchIntegrations = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetIntegrations();
            setIntegrations(response.data.data);
        } catch (error) {
            message.error('Failed to fetch integrations');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.adminGetIntegrationStats();
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const fetchTypes = async () => {
        try {
            const response = await api.adminGetIntegrationTypes();
            setTypes(response.data);
        } catch (error) {
            console.error('Failed to fetch types:', error);
        }
    };

    const handleCreate = () => {
        setEditingIntegration(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (integration) => {
        setEditingIntegration(integration);
        form.setFieldsValue(integration);
        setModalVisible(true);
    };

    const handleSubmit = async (values) => {
        try {
            if (editingIntegration) {
                await api.adminUpdateIntegration(editingIntegration.id, values);
                message.success('Integration updated successfully');
            } else {
                await api.adminCreateIntegration(values);
                message.success('Integration created successfully');
            }
            setModalVisible(false);
            fetchIntegrations();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to save integration');
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.adminDeleteIntegration(id);
            message.success('Integration deleted successfully');
            fetchIntegrations();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to delete integration');
        }
    };

    const handleToggle = async (id) => {
        try {
            await api.adminToggleIntegration(id);
            message.success('Integration status updated');
            fetchIntegrations();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to toggle integration');
        }
    };

    const handleTest = async (id) => {
        try {
            await api.adminTestIntegration(id);
            message.success('Integration test successful');
            fetchIntegrations();
        } catch (error) {
            message.error(error.message || 'Integration test failed');
        }
    };

    const statusColor = (status) => getStatusColor(status);

    const getTypeColor = (type) => {
        switch (type) {
            case 'payment': return 'blue';
            case 'social': return 'green';
            case 'analytics': return 'purple';
            case 'communication': return 'orange';
            case 'storage': return 'cyan';
            default: return 'default';
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'display_name',
            key: 'display_name',
            render: (text, record) => (
                <div>
                    <Text strong>{text}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.name}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag color={getTypeColor(type)}>
                    {types[type] || type}
                </Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={statusColor(status)}>
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Active',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Switch
                    checked={isActive}
                    checkedChildren={<CheckCircleOutlined />}
                    unCheckedChildren={<CloseCircleOutlined />}
                />
            ),
        },
        {
            title: 'Required',
            dataIndex: 'is_required',
            key: 'is_required',
            render: (isRequired) => (
                isRequired ? <Tag color="red">Required</Tag> : null
            ),
        },
        {
            title: 'Last Sync',
            dataIndex: 'last_sync_at',
            key: 'last_sync_at',
            render: (date) => date ? new Date(date).toLocaleString() : 'Never',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<PlayCircleOutlined />}
                        onClick={() => handleTest(record.id)}
                    >
                        Test
                    </Button>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        icon={record.is_active ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                        onClick={() => handleToggle(record.id)}
                    >
                        {record.is_active ? 'Disable' : 'Enable'}
                    </Button>
                    {!record.is_required && (
                        <Popconfirm
                            title="Are you sure you want to delete this integration?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            >
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="mb-6">
            <Title level={2}>Integrations</Title>
                <Text type="secondary">
                    Manage third-party integrations and services
                </Text>
            </div>

            {/* Statistics */}
            <Row gutter={16} className="mb-6">
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Total Integrations"
                            value={stats.total || 0}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Active"
                            value={stats.active || 0}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Inactive"
                            value={stats.inactive || 0}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="By Type"
                            value={Object.keys(stats.by_type || {}).length}
                        />
                    </Card>
                </Col>
            </Row>

            <Card>
                <div className="mb-4 flex justify-between items-center">
                    <Title level={4} className="!mb-0">All Integrations</Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        Add Integration
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={integrations}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} integrations`,
                    }}
                />
            </Card>

            <Modal
                title={editingIntegration ? 'Edit Integration' : 'Add Integration'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="name"
                        label="Integration Name"
                        rules={[{ required: true, message: 'Please enter integration name' }]}
                    >
                        <Input placeholder="e.g., razorpay, stripe" />
                    </Form.Item>

                    <Form.Item
                        name="display_name"
                        label="Display Name"
                        rules={[{ required: true, message: 'Please enter display name' }]}
                    >
                        <Input placeholder="e.g., Razorpay Payment Gateway" />
                    </Form.Item>

                    <Form.Item
                        name="type"
                        label="Type"
                        rules={[{ required: true, message: 'Please select type' }]}
                    >
                        <Select placeholder="Select integration type">
                            {Object.entries(types).map(([key, value]) => (
                                <Option key={key} value={key}>{value}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea rows={3} placeholder="Integration description" />
                    </Form.Item>

                    <Form.Item
                        name="is_active"
                        label="Active"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        name="is_required"
                        label="Required"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item className="mb-0">
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingIntegration ? 'Update' : 'Create'}
                            </Button>
                            <Button onClick={() => setModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Integrations;